import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import apiClient from '../services/apiClient';

interface Client {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  type: 'individual' | 'organization';
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
  notes?: string;
  cases?: string[]; // Array of case IDs
  documents?: string[]; // Array of document IDs
}

interface ClientsContextType {
  clients: Client[];
  loading: boolean;
  error: string | null;
  fetchClients: () => Promise<void>;
  addClient: (clientData: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateClient: (id: string, clientData: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  getClientById: (id: string) => Client | undefined;
  getClientCases: (id: string) => Promise<string[]>;
  getClientDocuments: (id: string) => Promise<string[]>;
}

const ClientsContext = createContext<ClientsContextType | undefined>(undefined);

export const useClients = () => {
  const context = useContext(ClientsContext);
  if (context === undefined) {
    throw new Error('useClients must be used within a ClientsProvider');
  }
  return context;
};

export const ClientsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const didInitRef = React.useRef(false);

  // ✅ FIX: Wrap fetchClients in useCallback to prevent infinite loops
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/clients');
      // Backend returns { success: true, data: { items: [...], total, page, limit } }
      const clientsArray = res.data?.items || res.data || res || [];
      setClients(Array.isArray(clientsArray) ? clientsArray : []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch clients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ FIX: Wrap all functions in useCallback to ensure stable references
  const addClient = useCallback(
    async (clientData: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) => {
      setLoading(true);
      try {
        const res = await apiClient.post('/api/clients', clientData);
        // Backend returns { success: true, data: { item: {...} } }
        const newClient = res?.data?.item || res?.data || res;
        setClients(prev => [...prev, newClient]);
        setError(null);
      } catch (err) {
        setError('Failed to add client');
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateClient = useCallback(async (id: string, clientData: Partial<Client>) => {
    setLoading(true);
    try {
      const res = await apiClient.patch(`/api/clients/${id}`, clientData);
      // Backend returns { success: true, data: {...client} }
      const updatedClient = res?.data || res;
      setClients(prev => prev.map(c => (c.id === id ? updatedClient : c)));
      setError(null);
    } catch (err) {
      setError('Failed to update client');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteClient = useCallback(async (id: string) => {
    setLoading(true);
    try {
      await apiClient.delete(`/api/clients/${id}`);
      setClients(prev => prev.filter(c => c.id !== id));
      setError(null);
    } catch (err) {
      setError('Failed to delete client');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getClientById = useCallback(
    (id: string) => {
      return clients.find(c => c.id === id);
    },
    [clients]
  );

  const getClientCases = useCallback(async (id: string) => {
    try {
      const res = await apiClient.get(`/api/clients/${id}/cases`);
      return res.data || res;
    } catch (err) {
      console.error('Failed to fetch client cases:', err);
      return [];
    }
  }, []);

  const getClientDocuments = useCallback(async (id: string) => {
    try {
      const res = await apiClient.get(`/api/clients/${id}/documents`);
      return res.data || res;
    } catch (err) {
      console.error('Failed to fetch client documents:', err);
      return [];
    }
  }, []);

  // ✅ FIX: Empty dependency array - fetchClients is now stable via useCallback
  // The didInitRef guard ensures single fetch even if component remounts
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    fetchClients();
  }, [fetchClients]);

  const value = {
    clients,
    loading,
    error,
    fetchClients,
    addClient,
    updateClient,
    deleteClient,
    getClientById,
    getClientCases,
    getClientDocuments,
  };

  return <ClientsContext.Provider value={value}>{children}</ClientsContext.Provider>;
};
